import { Injectable } from '@angular/core';

export interface PasswordRequirement {
  id: string;
  labelKey: string;
  test: (pw: string) => boolean;
}

export interface PasswordStrength {
  level: 0 | 1 | 2 | 3;
  labelKey: string;
  color: string;
}

export interface RequirementWithStatus extends PasswordRequirement {
  met: boolean;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: 'length',
    labelKey: 'create-password.req-length',
    test: pw => pw.length >= 12,
  },
  {
    id: 'uppercase',
    labelKey: 'create-password.req-uppercase',
    test: pw => /[A-Z]/.test(pw),
  },
  {
    id: 'lowercase',
    labelKey: 'create-password.req-lowercase',
    test: pw => /[a-z]/.test(pw),
  },
  {
    id: 'number',
    labelKey: 'create-password.req-number',
    test: pw => /[0-9]/.test(pw),
  },
  {
    id: 'special',
    labelKey: 'create-password.req-special',
    test: pw => /[^A-Za-z0-9]/.test(pw),
  },
];

@Injectable({ providedIn: 'root' })
export class PasswordValidationService {
  readonly requirements = PASSWORD_REQUIREMENTS;

  getRequirementsWithStatus(password: string): RequirementWithStatus[] {
    return PASSWORD_REQUIREMENTS.map(r => ({ ...r, met: r.test(password) }));
  }

  allRequirementsMet(password: string): boolean {
    return PASSWORD_REQUIREMENTS.every(r => r.test(password));
  }

  getPasswordStrength(password: string): PasswordStrength {
    if (password.length === 0) return { level: 0, labelKey: '', color: '' };

    let score = 0;
    const criteriaCount = PASSWORD_REQUIREMENTS.filter(r => r.test(password)).length;
    score += criteriaCount;

    if (password.length >= 16) score += 2;
    else if (password.length >= 14) score += 1;
    if (password.length >= 20) score += 1;

    const commonPatterns = /^(12345|abcdef|qwerty|password|azerty)/i;
    if (commonPatterns.test(password)) score = Math.max(score - 3, 1);

    const uniqueChars = new Set(password.toLowerCase()).size;
    if (uniqueChars <= 3) score = Math.max(score - 2, 1);

    if (score <= 3) return { level: 1, labelKey: 'create-password.strength-weak', color: '#e74c3c' };
    if (score <= 6) return { level: 2, labelKey: 'create-password.strength-medium', color: '#f39c12' };
    return { level: 3, labelKey: 'create-password.strength-strong', color: '#27ae60' };
  }
}
